/**
 * InvoiceFormActions
 *
 * Submit/cancel buttons bar. Also renders the "editing posted" warning banner
 * and the change notes field.
 */

import { Button, FormField, Textarea } from '../../ui'

import type { UseInvoiceFormReturn } from './useInvoiceForm'

interface InvoiceFormActionsProps {
  form: UseInvoiceFormReturn
  isLoading: boolean
  submitButtonLabel: string
  onCancel?: () => void
}

export function InvoiceFormActions({
  form,
  isLoading,
  submitButtonLabel,
}: InvoiceFormActionsProps): JSX.Element {
  const { isSubmitting, isEditingPosted, changeNotes, setChangeNotes } = form

  return (
    <>
      {isEditingPosted && (
        <div className="card el-p-md">
          <FormField hideMessage required label="Reason for Edit">
            <Textarea
              disabled={isLoading}
              placeholder="Explain why this posted invoice is being edited (required for audit trail)..."
              rows={2}
              value={changeNotes}
              onChange={(e) => setChangeNotes(e.target.value)}
            />
          </FormField>
        </div>
      )}

      <div className="flex justify-end el-gap-field pt-2 mt-1 border-t border-border">
        <Button
          disabled={isLoading || isSubmitting}
          type="submit"
          variant="primary"
        >
          {submitButtonLabel}
        </Button>
      </div>
    </>
  )
}

interface InvoiceFormPageActionsProps {
  initialDataId: string | undefined
  isSubmitting: boolean
  sellerOrgId: string | undefined
  onCancel: () => void
  onView?: () => void
  onDownloadPDF?: () => Promise<void>
  onShowVersionHistory: () => void
}

function ViewButton({ isSubmitting, onView }: { isSubmitting: boolean; onView: () => void }): JSX.Element {
  return (
    <Button disabled={isSubmitting} type="button" variant="secondary" onClick={onView}>
      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
      </svg>
      View
    </Button>
  )
}

function DownloadPDFButton({ isSubmitting, onDownloadPDF }: { isSubmitting: boolean; onDownloadPDF: () => Promise<void> }): JSX.Element {
  return (
    <Button disabled={isSubmitting} type="button" variant="primary" onClick={() => { void onDownloadPDF() }}>
      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
      </svg>
      Download PDF
    </Button>
  )
}

function VersionHistoryButton({ isSubmitting, onShowVersionHistory }: { isSubmitting: boolean; onShowVersionHistory: () => void }): JSX.Element {
  return (
    <Button disabled={isSubmitting} type="button" variant="secondary" onClick={onShowVersionHistory}>
      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
      </svg>
      Version History
    </Button>
  )
}

export function InvoiceFormPageActions({
  initialDataId,
  isSubmitting,
  sellerOrgId,
  onCancel,
  onView,
  onDownloadPDF,
  onShowVersionHistory,
}: InvoiceFormPageActionsProps): JSX.Element {
  return (
    <>
      {initialDataId && onView && <ViewButton isSubmitting={isSubmitting} onView={onView} />}
      {initialDataId && onDownloadPDF && (
        <DownloadPDFButton isSubmitting={isSubmitting} onDownloadPDF={onDownloadPDF} />
      )}
      {initialDataId && sellerOrgId && (
        <VersionHistoryButton isSubmitting={isSubmitting} onShowVersionHistory={onShowVersionHistory} />
      )}
      <Button disabled={isSubmitting} type="button" variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
    </>
  )
}
